import {
    Form,
    FormType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { Component, OnInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BaseForm } from 'src/app/view/base-form';
import { list } from 'src/app/common/classes/request-builder';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from 'src/app/service/events/dashboard.service';
import { UsuariosApiService } from 'src/app/service/api/usuarios-api.service';
import paises from 'src/app/common/country';
import { StorageManager } from 'src/app/common/storage-manager.class';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';

import { UserModel } from 'src/app/models/usuarios/user.model';
import { UsuariosFormDefinition, UsuariosFormDefinitionContext } from './form-control/form-definition';

@Component({
    selector: 'app-usuarios-form',
    standalone: false,
    templateUrl: './usuarios-form.component.html',
    styleUrls: ['./usuarios-form.component.scss']
})
export class UsuariosFormComponent extends BaseForm<UserModel> implements OnInit {
    public appName = environment.appName;
    public applicationType = environment.features.applicationType;
    public clienteName: string = environment.features.clienteName;
    public showPerfilUnidades = environment.features.showPerfilUnidades;
    public lista_paises = list<string | null>(['...', null]);

    public override model: UserModel = {} as UserModel;
    public reactiveForm!: { formType: FormType; model: Partial<UserModel>; formFields: Form<UserModel> };
    public formGetRequest!: RequestHandler<UserModel[]>;
    public nifErrors = '';
    public passErrors = '';
    public showPassErrors = false;
    public isShowingPassword = false;

    public canRegister = environment.features.canRegister;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public usuariosApi: UsuariosApiService,
        public override router: Router
    ) {
        super(
            route,
            router,
            dashboard,
            usuariosApi.usuariosPUT,
            usuariosApi.usuariosPOST,
            'usuarios',
            'Actualizar Usuario',
            'Crear Usuario'
        );

        this.formGetRequest = this.resolveFormGetRequest();
    }

    async ngOnInit() {
        if (this.appName === 'manezylozano') {
            await this.fillCountry();
        }

        this.createReactiveForm();
        this.softInit(this.getType());
    }

    public reactiveFormChanges(
        formModel: ReactiveFormValue<UserModel>,
        lastChangedFieldName: ReactiveFormChangedField<UserModel>,
    ): void {
        this.model = { ...this.model, ...formModel } as UserModel;
        (this.formChanges as (tag?: string) => void).call(this, String(lastChangedFieldName ?? ''));
        if (this.reactiveForm) {
            this.reactiveForm.model = this.model;
        }
    }

    public formChanges(): void {}

    public validateNif(): boolean {
        if (this.getType() === FormType.CREATION) {
            const nifSearch = ((this.model || {}).nif || '').toLowerCase();

            if ((nifSearch || '').length === 0) {
                return true;
            }

            const existeNif = false;

            if (existeNif) {
                this.nifErrors = 'El usuario ya existe';
            } else {
                this.nifErrors = '';
            }

            return !existeNif;
        }

        return true;
    }

    public validatePassword(): boolean {
        this.showPassErrors = false;
        this.passErrors = '';

        if (this.model.password && (this.model.password.trim().length > 0)) {
            if (!this.model.password_repeat) {
                this.passErrors += 'Por favor, repita la contraseña.\n';
                return false;
            }
            if (this.model.password !== this.model.password_repeat) {
                this.passErrors += 'Las contraseñas no coinciden.\n';
                return false;
            }
            return true;
        }
        return true;
    }

    public togglePassword(): void {
        if (this.getType() === FormType.CREATION) {
            return;
        }
        if (!this.model.password) {
            this.usuariosApi.usuariosGET.toPromise({ id: this.model.id, p3: StorageManager.getUser().id } as never).then(result => {
                if (result) {
                    this.model.password_old = result[0]?.password === null ? 'Sin Contraseña' : result[0]?.password ?? '';
                }
                return;
            }
            ).catch(e => {
                console.log('catch en getPosition: ' + e);
            }
            );
        }
        this.isShowingPassword = !this.isShowingPassword;
    }

    fillCountry(): Promise<boolean> {
        return new Promise((resolve) => {
            for (let index = 0; index < paises.length; index++) {
                const pais = { label: paises[index]?.label, value: paises[index]?.value };

                this.lista_paises.filtered.push({
                    label: pais.label ?? '',
                    value: pais.value ?? null,
                });
                if ((index + 1) >= paises.length) {
                    resolve(true);
                }
            }
        });
    }

    private resolveFormGetRequest(): RequestHandler<UserModel[]> {
        // The list endpoint intentionally removes credentials. The form must hydrate from the generic users endpoint
        // so password and repeated password can stay synchronized while editing existing records.
        return this.usuariosApi.usuariosGenericGET;
    }

    private createReactiveForm(): void {
        const formType = Number(this.getType()) as FormType;
        const definition = new UsuariosFormDefinition(formType, this.model, this.createFormDefinitionContext());

        this.model = definition.model as UserModel;
        this.reactiveForm = {
            formType,
            model: this.model,
            formFields: definition.formFields,
        };
    }

    private createFormDefinitionContext(): UsuariosFormDefinitionContext {
        return {
            appName: this.appName,
            applicationType: this.applicationType,
            canRegister: this.canRegister,
            clienteName: this.clienteName,
            showPerfilUnidades: this.showPerfilUnidades,
            countryList: this.lista_paises,
            recordId: this.getRegisterId(),
        };
    }
}
